/** The regimes the product has loaded - `GET /api/v1/regimes` (**ADR-0205**).
 *
 * The SPA used to hold the regime keys as a union of three literals (the generated type, then a cast
 * in `useWriteStatement`), a label table in `organisationOptions.ts` and a per-regime "leading
 * questions" map in `Financials.tsx`. A fourth regime loaded as data would have been invisible to
 * every picker and a type error in the one that was typed. Now there is one source: the regime rows,
 * through this read. A key is an opaque string everywhere in the SPA; its name, its jurisdiction,
 * the level the product publishes for it and the yes/no questions its rules ask all arrive here.
 *
 * Thrown on failure, never defaulted - `data ?? []` would make a failed read look like "no regime is
 * loaded", which is the failure `Loaded` and `ReadFailed` exist to tell apart.
 */

import { useQuery } from "@tanstack/react-query";

import { api } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type Regime = components["schemas"]["RegimeOut"];

export const regimesQuery = {
  queryKey: ["regimes"] as const,
  queryFn: async (): Promise<Regime[]> => {
    const { data, error, response } = await api.GET("/api/v1/regimes", {});
    if (error || !data) {
      throw refusalFrom(response, error, "The regimes could not be read.");
    }
    return data;
  },
  // Reference data: it changes when rules are loaded, which is a deploy.
  staleTime: 10 * 60_000,
  retry: false,
} as const;

export function useRegimes() {
  return useQuery(regimesQuery);
}

/** A regime as a person reads it. An unknown key falls back to itself rather than to a blank: a
 * regime the list has not (yet) returned must still be shown, and a guess would be a second home. */
export function regimeLabel(key: string, regimes: readonly Regime[] | undefined): string {
  return regimes?.find((regime) => regime.key === key)?.label ?? key;
}
